import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/apiConfig';
import '../styles/PurchaseInvoicePreview.css';

const PurchaseInvoicePreview = ({ data: initialData, onBack, onEdit }) => {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFullData = async () => {
            const poId = initialData?._id || initialData?.id;
            // Determine if it's a Challan or an Order/Invoice
            const isChallan = initialData?.challan_no || initialData?.challanNo || false;
            
            if (poId) {
                setLoading(true);
                try {
                    const token = sessionStorage.getItem('token');
                    const endpoint = isChallan ? 'purchase-challan' : 'purchase-order';
                    const res = await axios.get(`${API_BASE_URL}/${endpoint}/${poId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data) {
                        setData(res.data);
                    }
                } catch (err) {
                    console.error("Error fetching full PO/Challan data:", err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchFullData();
    }, [initialData]);

    // Helper to convert number to words (Indian System)
    const numberToWords = (num) => {
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const inWords = (n) => {
            if (n < 20) return a[n];
            let s = b[Math.floor(n / 10)];
            if (n % 10 > 0) s += ' ' + a[n % 10].trim();
            return s;
        };

        const convert = (n) => {
            if (n === 0) return '';
            let res = '';
            if (n >= 10000000) {
                res += convert(Math.floor(n / 10000000)) + 'Crore ';
                n %= 10000000;
            }
            if (n >= 100000) {
                res += inWords(Math.floor(n / 100000)) + ' Lakh ';
                n %= 100000;
            }
            if (n >= 1000) {
                res += inWords(Math.floor(n / 1000)) + ' Thousand ';
                n %= 1000;
            }
            if (n >= 100) {
                res += inWords(Math.floor(n / 100)) + ' Hundred ';
                n %= 100;
            }
            if (n > 0) {
                if (res !== '') res += 'and ';
                res += inWords(Math.floor(n));
            }
            return res;
        };

        if (num === 0) return 'Zero Only';
        const parts = num.toFixed(2).split('.');
        let result = convert(parseInt(parts[0]));
        if (parts.length > 1 && parseInt(parts[1]) > 0) {
            result += ' and ' + inWords(parseInt(parts[1])) + ' Paise';
        }
        return result.trim() + ' Only';
    };

    // Correctly calculate Item Sum from items array instead of using grand total
    const productItemsArr = (() => {
        const itemsRaw = data?.items;
        let itemsArray = [];
        try {
            itemsArray = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : (itemsRaw || []);
        } catch (e) { itemsArray = []; }
        return Array.isArray(itemsArray) ? itemsArray : [];
    })();

    const calculatedItemSum = productItemsArr.reduce((sum, item) => sum + (parseFloat(item.total) || parseFloat(item.amount) || 0), 0);
    const itemSum = calculatedItemSum;

    const discountPercentage = parseFloat(data?.discount || 0);
    const discountAmount = data?.discount_type === '%' ? (itemSum * discountPercentage) / 100 : discountPercentage;

    const freightPercentage = parseFloat(data?.freight || 0);
    const freightAmount = (itemSum * freightPercentage) / 100;
    const insurancePercentage = parseFloat(data?.insurance || 0);
    const insuranceAmount = (itemSum * insurancePercentage) / 100;
    const extraCharges = parseFloat(data?.extr_charges_amount || data?.extraChargesAmount || 0);

    // New Subtotal = Item Sum - Discount + Extra Charges + Freight + Insurance
    const subTotal = itemSum - discountAmount + extraCharges + freightAmount + insuranceAmount;

    // Detect Local vs Interstate based on Sale Type name (highest priority for user logic)
    const saleTypeName = (data?.saleType || data?.sale_type_name || '').toLowerCase();
    const isInterstateManual = saleTypeName.includes('interstate');
    const isLocalManual = saleTypeName.includes('local');

    // Use saved percentages but override based on "Interstate" keyword if necessary
    const rawTax1 = data?.tax1 !== undefined && data?.tax1 !== null ? data.tax1 : (data?.sale_type_tax1 || 0);
    const rawTax2 = data?.tax2 !== undefined && data?.tax2 !== null ? data.tax2 : (data?.sale_type_tax2 || 0);
    
    let taxPercentage1 = parseFloat(rawTax1);
    let taxPercentage2 = parseFloat(rawTax2);

    // STRICT USER LOGIC: If Interstate is selected, STATE/UT TAX must be 0
    if (isInterstateManual) {
        taxPercentage2 = 0;
    }

    const companyStateStr = (data?.companyState || data?.company_state || data?.buyerState || data?.buyer_state || '').toLowerCase();
    const isLocal = !isInterstateManual && (isLocalManual || companyStateStr.includes('gujarat') || companyStateStr === '');
    
    const dbCgst = data?.central_tax !== undefined && data?.central_tax !== null ? parseFloat(data.central_tax) : null;
    const dbSgst = data?.state_tax !== undefined && data?.state_tax !== null ? parseFloat(data.state_tax) : null;

    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;

    if (isLocal) {
        cgstAmount = dbCgst !== null ? dbCgst : (subTotal * taxPercentage1) / 100;
        sgstAmount = dbSgst !== null ? dbSgst : (subTotal * taxPercentage2) / 100;
    } else {
        igstAmount = (dbCgst !== null || dbSgst !== null) 
            ? ((dbCgst || 0) + (dbSgst || 0)) 
            : (subTotal * (taxPercentage1 + taxPercentage2)) / 100;
    }
    const totalTax = cgstAmount + sgstAmount + igstAmount;

    // Final Grand Total
    const grandTotal = parseFloat(data?.total_amount) || (subTotal + totalTax);

    // Build invoice object from dynamic data prop
    const invoice = {
        companyName: data?.companyName || data?.company_name || 'No Name Found',
        address: data?.companyAddress || data?.company_address || '',
        city: `${data?.companyCity || data?.company_city || ''} ${data?.companyState || data?.company_state || ''} ${data?.companyPinCode || data?.company_pin_code || ''}`,
        mobile: `Mo.: ${data?.companyMobileNumber || data?.company_mobile_number || ''}`,
        email: `E-mail: ${data?.companyEmail || data?.company_email || ''}`,
        invoiceType: (data?.challan_no || data?.challanNo) ? 'CHALLAN' : 'TAX INVOICE',

        // Company (Seller) details
        pan: data?.companyPanCardNumber || data?.company_pan_card_number || '',
        gstin: data?.companyGstNumber || data?.company_gst_number || '',
        cin: data?.companyCinNumber || data?.company_cin_number || '',

        // Invoice details
        invoiceNo: data?.invoiceNo || data?.invoice_no || data?.challanNo || data?.challan_no || '',
        invoiceDate: (data?.invoiceDate || data?.invoice_date || data?.challanDate || data?.challan_date) 
            ? new Date(data.invoiceDate || data.invoice_date || data.challanDate || data.challan_date).toLocaleDateString('en-GB') 
            : '',

        // Buyer details (Own Company)
        buyer: {
            name: data?.buyerTradeName || data?.buyer_trade_name || '',
            address: data?.buyerAddress || data?.buyer_address || '',
            city: [
                data?.buyerCity || data?.buyer_city,
                data?.buyerState || data?.buyer_state,
                data?.buyerPinCode || data?.buyer_pin_code
            ].filter(Boolean).join(' '),
            pan: data?.buyerPanCardNumber || data?.buyer_pan_card_number || '',
            gstin: data?.buyerGstNumber || data?.buyer_gst_number || '',
            cin: data?.buyerCinNumber || data?.buyer_cin_number || '',
            placeOfSupply: data?.buyerState || data?.buyer_state || ''
        },

        // Consignee details (Assuming same as buyer for now)
        consignee: {
            name: data?.buyerTradeName || data?.buyer_trade_name || '',
            address: data?.buyerAddress || data?.buyer_address || '',
            city: [
                data?.buyerCity || data?.buyer_city,
                data?.buyerState || data?.buyer_state,
                data?.buyerPinCode || data?.buyer_pin_code
            ].filter(Boolean).join(' '),
            pan: data?.buyerPanCardNumber || data?.buyer_pan_card_number || '',
            gstin: data?.buyerGstNumber || data?.buyer_gst_number || '',
            cin: data?.buyerCinNumber || data?.buyer_cin_number || '',
            placeOfSupply: data?.buyerState || data?.buyer_state || ''
        },

        // Transport details
        vehicleNo: data?.vehicleNo || data?.vehicle_no || '',
        lrNo: data?.lrNo || data?.lr_no || '',
        eWayBillNo: data?.eWayBillNo || data?.e_way_bill_no || '',

        // Items
        items: (() => {
            const itemsRaw = data?.items;
            let itemsArray = [];
            try {
                itemsArray = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : (itemsRaw || []);
            } catch (e) {
                console.error("Error parsing items:", e);
            }
            return Array.isArray(itemsArray) ? itemsArray : [];
        })().map((item, idx) => ({
            srNo: idx + 1,
            hsnCode: item.hsnCode || item.hsn_code || '',
            category: item.category || '',
            product: item.product || '',
            color: item.color || '',
            grade: item.grade || '',
            qty: item.quantity || 0,
            unit: item.unit || 'PCS',
            rate: item.rate || 0,
            amount: item.total || 0
        })),

        // Totals
        totals: {
            totalQty: (() => {
                const itemsRaw = data?.items;
                let itemsArray = [];
                try {
                    itemsArray = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : (itemsRaw || []);
                } catch (e) {
                    itemsArray = [];
                }
                const arr = Array.isArray(itemsArray) ? itemsArray : [];
                return arr.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
            })(),
            discountAmount: discountAmount.toFixed(2),
            itemSum: itemSum.toFixed(2),
            freightPercent: freightPercentage,
            freight: freightAmount.toFixed(2),
            insurancePercent: insurancePercentage,
            insurance: insuranceAmount.toFixed(2),
            subTotal: subTotal.toFixed(2),
            isLocal,
            tax1Percent: taxPercentage1,
            tax2Percent: taxPercentage2,
            interstateTaxPercent: (taxPercentage1 + taxPercentage2),
            cgst: cgstAmount.toFixed(2),
            sgst: sgstAmount.toFixed(2),
            igst: igstAmount.toFixed(2),
            extraCharges: extraCharges.toFixed(2),
            totalTax: totalTax.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
            amountInWords: numberToWords(grandTotal)
        },

        // Bank details
        bankName: data?.buyerBankName || data?.buyer_bank_name || '',
        accountName: data?.buyerBankAccountName || data?.buyer_bank_account_name || '',
        accountNumber: data?.buyerAccountNo || data?.buyer_account_no || '',
        ifscCode: data?.buyerIfscCode || data?.buyer_ifsc_code || '',

        // Footer
        remarks: data?.remarks || '',
        termsAndConditions: data?.termsCondtion || data?.terms_condtion || ''
    };

    // Handle Print functionality
    const handlePrint = () => {
        window.print();
    };

    // Handle PDF Download using jsPDF
    const handleDownloadPDF = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;
        const elements = document.querySelectorAll('.invoice-wrapper');

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];
            el.classList.add('pdf-capture-mode');

            try {
                // Wait a tiny bit for the browser to apply the PDF mode CSS rules
                await new Promise(resolve => setTimeout(resolve, 100));

                const canvas = await html2canvas(el, { 
                    scale: 2, 
                    useCORS: true, 
                    logging: false, 
                    backgroundColor: '#ffffff',
                    width: 794,
                    height: 1123,
                    windowWidth: 794,
                    windowHeight: 1123
                });

                const imgData = canvas.toDataURL('image/png');
                
                if (i > 0) {
                    pdf.addPage();
                }

                const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
                const margin = 4; // 4mm margin on all sides
                const renderWidth = pdfWidth - (margin * 2);
                
                const imgProps = pdf.getImageProperties(imgData);
                const renderHeight = (imgProps.height * renderWidth) / imgProps.width; // Restore perfect aspect ratio to eliminate font distortion

                pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);
            } finally {
                el.classList.remove('pdf-capture-mode');
            }
        }
        pdf.save(`Invoice_${invoice.invoiceNo.replace(/\//g, '-')}.pdf`);
    };


    // Helper to chunk array
    const chunkArray = (array, size) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    };

    const itemChunks = invoice.items && invoice.items.length > 0 
        ? chunkArray(invoice.items, 10) 
        : [[]];

    if (loading) return <div className="purchase-invoice-preview">Loading...</div>;

    return (
        <div className="purchase-invoice-preview">
            <div className="preview-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '20px' }}>
                <div className="invoice-pages-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {itemChunks.map((chunk, pageIndex) => (
                        <div className="invoice-wrapper" key={pageIndex}>
                            <div className="invoice-box">
                                <div className="invoice-header">
                                    <h1 className="company-name">{invoice.companyName}</h1>
                                    <p className="company-address">{invoice.address}</p>
                                    <p className="company-address">{invoice.city}</p>
                                    <p className="company-contact">{invoice.mobile} {invoice.email}</p>
                                    <h2 className="invoice-type">{invoice.invoiceType}</h2>
                                </div>

                                <div className="details-row">
                                    <div className="details-left">
                                        <div className="detail-item">PAN NO : {invoice.pan}</div>
                                        <div className="detail-item">GSTIN NO : {invoice.gstin}</div>
                                        <div className="detail-item">CIN/LLP NO : {invoice.cin}</div>
                                    </div>
                                    <div className="details-right">
                                        <div className="detail-item">ORIGINAL FOR RECIPIENT [ ]</div>
                                        <div className="detail-item">DUPALICATE FOR TRANSPORTER [ ]</div>
                                        <div className="detail-item">TRIPALICATE FOR SUPPLIER [ ]</div>
                                    </div>
                                </div>

                                <div className="info-section">
                                    <div className="info-left">
                                        <div className="section-title">DETAILS OF RECEIVER (BUYER DETAIL)</div>
                                        <div className="info-content">
                                            <strong>{invoice.buyer.name}</strong><br />
                                            {invoice.buyer.address}<br />
                                            {invoice.buyer.city}<br />
                                            <strong>PAN NO:</strong> {invoice.buyer.pan}<br />
                                            <strong>GSTIN NO:</strong> {invoice.buyer.gstin}<br />
                                            <strong>PLACE OF SUPPLY:</strong> {invoice.buyer.placeOfSupply}
                                        </div>
                                    </div>
                                    <div className="info-right">
                                        <div className="invoice-detail"><strong>INVOICE NO.:</strong> {invoice.invoiceNo}</div>
                                        <div className="invoice-detail"><strong>INVOICE DATE:</strong> {invoice.invoiceDate}</div>
                                    </div>
                                </div>

                                <div className="info-section">
                                    <div className="info-left">
                                        <div className="section-title">DETAILS OF CONSIGNEE (SHIPPED DETAIL)</div>
                                        <div className="info-content">
                                            <strong>{invoice.consignee.name}</strong><br />
                                            {invoice.consignee.address}<br />
                                            {invoice.consignee.city}<br />
                                            <strong>PAN NO:</strong> {invoice.consignee.pan}<br />
                                            <strong>GSTIN NO:</strong> {invoice.consignee.gstin}<br />
                                            <strong>PLACE OF SUPPLY:</strong> {invoice.consignee.placeOfSupply}
                                        </div>
                                    </div>
                                    <div className="info-right">
                                        <div className="invoice-detail"><strong>VEHICLE NO.:</strong> {invoice.vehicleNo}</div>
                                        <div className="invoice-detail"><strong>L.R. NO:</strong> {invoice.lrNo}</div>
                                        <div className="invoice-detail"><strong>E-WAY BILL NO:</strong> {invoice.eWayBillNo}</div>
                                    </div>
                                </div>

                                <div className="invoice-table-container">
                                    <table className="invoice-table">
                                        <thead>
                                            <tr>
                                                <th>SR NO.</th>
                                                <th>CATEGORY</th>
                                                <th>PRODUCT NAME</th>
                                                <th>HSN CODE</th>
                                                <th>COLOR</th>
                                                <th>GRADE</th>
                                                <th>QTY</th>
                                                <th>UNIT</th>
                                                <th>RATE</th>
                                                <th>AMOUNT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chunk.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.srNo}</td>
                                                    <td>{item.category}</td>
                                                    <td>{item.product}</td>
                                                    <td>{item.hsnCode}</td>
                                                    <td>{item.color}</td>
                                                    <td>{item.grade}</td>
                                                    <td>{item.qty}</td>
                                                    <td>{item.unit}</td>
                                                    <td className="text-right">{item.rate}</td>
                                                    <td className="text-right">{item.amount}</td>
                                                </tr>
                                            ))}
                                            {/* Filler row for vertical lines */}
                                            <tr className="filler-row-preview">
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                                <td style={{ borderBottom: 'none', borderRight: '1px solid #000' }}></td>
                                                <td style={{ borderBottom: 'none' }}></td>
                                            </tr>
                                            <tr className="total-row-preview">
                                                <td colSpan="6" className="text-center"><strong>TOTAL</strong></td>
                                                <td><strong>{invoice.totals.totalQty}</strong></td>
                                                <td></td>
                                                <td></td>
                                                <td className="text-right"><strong>{invoice.totals.itemSum}</strong></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="invoice-footer">
                                    <div className="footer-left">
                                        <div className="footer-row"><span className="footer-label">REMARKS:</span><span className="footer-value">{invoice.remarks}</span></div>
                                        <div className="footer-row"><span className="footer-label">RUPEES IN WORDS:</span><span className="footer-value"><strong>{invoice.totals.amountInWords}</strong></span></div>
                                        <div className="footer-row"><span className="footer-label">BANK NAME:</span><span className="footer-value">{invoice.bankName} (A/C: {invoice.accountNumber}, IFSC: {invoice.ifscCode})</span></div>
                                        <div className="footer-terms"><strong>TERMS & CONDITIONS:</strong><p>{invoice.termsAndConditions}</p></div>
                                    </div>
                                    <div className="footer-right">
                                        <div className="amount-row"><span>FREIGHT</span><span className="text-right">{invoice.totals.freight}</span></div>
                                        <div className="amount-row"><span>INSURANCE</span><span className="text-right">{invoice.totals.insurance}</span></div>
                                        {parseFloat(invoice.totals.discountAmount) > 0 && (
                                            <div className="amount-row"><span>DISCOUNT</span><span className="text-right" style={{color: '#ef4444'}}>-{invoice.totals.discountAmount}</span></div>
                                        )}
                                        {parseFloat(invoice.totals.extraCharges) > 0 && (
                                            <div className="amount-row"><span>EXTRA CHARGES</span><span className="text-right">{invoice.totals.extraCharges}</span></div>
                                        )}
                                        <div className="amount-row"><strong>TAXABLE AMOUNT</strong><span className="text-right"><strong>{invoice.totals.subTotal}</strong></span></div>
                                        {(parseFloat(invoice.totals.cgst) > 0 || (invoice.totals.tax1Percent > 0 && invoice.totals.isLocal)) && (
                                            <div className="amount-row"><span>CENTRAL TAX ({invoice.totals.tax1Percent}%)</span><span className="text-right">{invoice.totals.cgst}</span></div>
                                        )}
                                        {(parseFloat(invoice.totals.sgst) > 0 || (invoice.totals.tax2Percent === 0 && invoice.totals.tax1Percent > 0 && invoice.totals.isLocal)) && (
                                            <div className="amount-row"><span>STATE/UT TAX ({invoice.totals.tax2Percent}%)</span><span className="text-right">{invoice.totals.sgst}</span></div>
                                        )}
                                        {(parseFloat(invoice.totals.igst) > 0 || (!invoice.totals.isLocal && invoice.totals.interstateTaxPercent > 0)) && (
                                            <div className="amount-row"><span>INTERSTATE TAX ({invoice.totals.interstateTaxPercent || (parseFloat(invoice.totals.tax1Percent) + parseFloat(invoice.totals.tax2Percent))}%)</span><span className="text-right">{invoice.totals.igst}</span></div>
                                        )}
                                        <div className="amount-row grand-total"><strong>BILL AMOUNT</strong><span className="text-right"><strong>{invoice.totals.grandTotal}</strong></span></div>
                                        <div className="signature-section"><p>FOR, {invoice.companyName}</p><p className="signature-line">[AUTHORISED SIGNATORY]</p></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="action-sidebar">
                    <button className="action-btn" onClick={onBack}>Back</button>
                    <button className="action-btn" onClick={onEdit}>Update</button>
                    <button className="action-btn" onClick={handleDownloadPDF}>PDF</button>
                    <button className="action-btn" onClick={handlePrint}>Print</button>
                </div>
            </div>
        </div>
    );
};

export default PurchaseInvoicePreview;
